"use client";

import { useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { ChainResults } from "@/components/tools/chemistry/dimensional-results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { COMMON_FACTORS } from "@/lib/tool-kit/chemistry/common-factors";
import {
  type Evaluated,
  evaluateInputs,
  type FactorRow,
} from "@/lib/tool-kit/chemistry/dimensional-input";
import { fewestSigFigs } from "@/lib/tool-kit/chemistry/format";

/** English strings. A translated content file overrides these through `ui`. */
export const DIMENSIONAL_ANALYSIS_CALCULATOR_STRINGS = {
  startValue: "Starting value",
  startUnits: "Starting units",
  startUnitsHint:
    "Write units with / and ·, and powers as ^2 or ²: mi/h, kg·m/s², J/(mol·K), cm3.",
  targetUnits: "Convert to (optional)",
  targetHint: "Fill this in to have the answer's units checked.",
  factorsHeading: "Conversion factors, in order",
  factorHint:
    "Each row is an equality such as 1 mi = 1609.344 m. The calculator turns it the right way up to cancel units.",
  leftValue: "Conversion {number}: left value",
  leftUnits: "Conversion {number}: left units",
  rightValue: "Conversion {number}: right value",
  rightUnits: "Conversion {number}: right units",
  exact: "Exact",
  exactLabel: "Conversion {number} is exact",
  removeRow: "Remove conversion {number}",
  addRow: "Add a conversion",
  addCommon: "Add a common conversion",
  addCommonPlaceholder: "Choose one…",
  sigFigs: "Significant figures in the answer",
  sigMatch: "Match my data",
  sigChoice:
    "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  answer: "Answer",
  unitsOfAnswer: "Units of the answer",
  chainHeading: "The chain",
  cancelled: "cancelled",
  unrounded: "Unrounded: {value}",
  targetOk: "The units match the target, {target}.",
  targetMismatch:
    "The answer is in {units}, not {target}. A unit is left over: add a conversion that removes it, or check the direction of one.",
  exactNote:
    'Exact conversion factors (definitions) do not limit the significant figures. Untick "Exact" for a measured factor such as a molar mass.',
  enterStart: "Enter a starting value and its units.",
  badStartValue: "The starting value must be a number.",
  badStartUnits:
    "The starting units could not be read. Try mi/h, g, kg·m/s² or cm3.",
  badTargetUnits: "The target units could not be read.",
  incompleteRow: "Conversion {number} needs a value and units on both sides.",
  badRowUnits: "The units in conversion {number} could not be read.",
  badFactor: "Conversion {number} has a value that is zero or not a number.",
  noCommonUnit:
    "Conversion {number} has no unit in common with the quantity so far, so it cannot cancel anything. Check the order of the conversions.",
  copy: "Copy the calculation",
  copied: "Copied!",
  commonLabels: {
    "in-cm": "1 in = 2.54 cm",
    "ft-in": "1 ft = 12 in",
    "yd-ft": "1 yd = 3 ft",
    "mi-ft": "1 mi = 5280 ft",
    "mi-m": "1 mi = 1609.344 m",
    "m-cm": "1 m = 100 cm",
    "km-m": "1 km = 1000 m",
    "kg-g": "1 kg = 1000 g",
    "g-mg": "1 g = 1000 mg",
    "lb-kg": "1 lb = 0.45359237 kg",
    "oz-g": "1 oz = 28.349523125 g",
    "L-mL": "1 L = 1000 mL",
    "gal-L": "1 gal (US) = 3.785411784 L",
    "h-min": "1 h = 60 min",
    "min-s": "1 min = 60 s",
    "day-h": "1 day = 24 h",
    "cal-J": "1 cal = 4.184 J",
    "atm-Pa": "1 atm = 101325 Pa",
    "atm-Torr": "1 atm = 760 Torr",
    "bar-Pa": "1 bar = 100000 Pa",
    "mol-N": "1 mol = 6.02214076 × 10²³ particles",
  },
  particlesUnit: "particles",
} as const;

type Strings = typeof DIMENSIONAL_ANALYSIS_CALCULATOR_STRINGS;

const SIG_CHOICES = ["3", "4", "5", "6"] as const;
const DEFAULT_SIG = 4;
const MAX_ROWS = 10;
const START_ROWS: FactorRow[] = [
  { id: 1, lv: "1", lu: "mi", rv: "1609.344", ru: "m", exact: true },
  { id: 2, lv: "1", lu: "h", rv: "3600", ru: "s", exact: true },
];

function Problem({
  evaluation,
  strings,
  number,
}: {
  evaluation: Extract<Evaluated, { ok: false }>;
  strings: Strings;
  number: string;
}) {
  const { code } = evaluation.problem;
  if (code === "enterStart") {
    return <Notice>{strings.enterStart}</Notice>;
  }
  return <ErrorNotice>{t(strings[code], { number })}</ErrorNotice>;
}

function chosenSig(evaluation: Evaluated, choice: string): number {
  if (!evaluation.ok) {
    return DEFAULT_SIG;
  }
  return choice === "match"
    ? fewestSigFigs(evaluation.measured, DEFAULT_SIG)
    : Number(choice);
}

export default function DimensionalAnalysisCalculatorWidget() {
  const strings = useToolUi(DIMENSIONAL_ANALYSIS_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [startText, setStartText] = useState("60.0");
  const [startUnitsText, setStartUnitsText] = useState("mi/h");
  const [targetText, setTargetText] = useState("m/s");
  const [rows, setRows] = useState<FactorRow[]>(START_ROWS);
  const [nextId, setNextId] = useState(START_ROWS.length + 1);
  const [sigChoice, setSigChoice] = useState("match");

  const update = (id: number, patch: Partial<FactorRow>) =>
    setRows(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  const addRow = (row: Omit<FactorRow, "id">) => {
    setRows([...rows, { id: nextId, ...row }]);
    setNextId(nextId + 1);
  };
  const addCommon = (id: string) => {
    const found = COMMON_FACTORS.find((c) => c.id === id);
    if (found && rows.length < MAX_ROWS) {
      addRow({
        lv: found.leftValue,
        lu: found.leftUnit,
        rv: found.rightValue,
        ru:
          found.rightUnit === "particles"
            ? strings.particlesUnit
            : found.rightUnit,
        exact: true,
      });
    }
  };

  const evaluation = evaluateInputs({
    startText,
    startUnitsText,
    targetText,
    rows,
  });
  const sig = chosenSig(evaluation, sigChoice);
  const problemRow =
    !evaluation.ok && "row" in evaluation.problem ? evaluation.problem.row : 0;

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          id="da-start"
          label={strings.startValue}
          numeric
          onChange={setStartText}
          value={startText}
        />
        <TextField
          hint={strings.startUnitsHint}
          id="da-start-units"
          label={strings.startUnits}
          mono
          onChange={setStartUnitsText}
          value={startUnitsText}
        />
        <TextField
          hint={strings.targetHint}
          id="da-target"
          label={strings.targetUnits}
          mono
          onChange={setTargetText}
          value={targetText}
        />
      </div>
      <div className="space-y-3">
        <p className={LABEL}>{strings.factorsHeading}</p>
        <p className="text-muted-foreground text-xs">{strings.factorHint}</p>
        {rows.map((row, index) => {
          const number = fmt(index + 1);
          return (
            <div
              className="grid grid-cols-2 items-end gap-3 sm:grid-cols-[1fr_1.4fr_auto_1fr_1.4fr_auto_auto]"
              key={row.id}
            >
              <TextField
                id={`da-lv-${row.id}`}
                label={t(strings.leftValue, { number })}
                numeric
                onChange={(lv) => update(row.id, { lv })}
                value={row.lv}
              />
              <TextField
                id={`da-lu-${row.id}`}
                label={t(strings.leftUnits, { number })}
                mono
                onChange={(lu) => update(row.id, { lu })}
                value={row.lu}
              />
              <span
                aria-hidden="true"
                className="hidden pb-2 text-center sm:block"
              >
                =
              </span>
              <TextField
                id={`da-rv-${row.id}`}
                label={t(strings.rightValue, { number })}
                numeric
                onChange={(rv) => update(row.id, { rv })}
                value={row.rv}
              />
              <TextField
                id={`da-ru-${row.id}`}
                label={t(strings.rightUnits, { number })}
                mono
                onChange={(ru) => update(row.id, { ru })}
                value={row.ru}
              />
              <span className="pb-2">
                <Checkbox
                  checked={row.exact}
                  label={
                    <>
                      {strings.exact}
                      <span className="sr-only">
                        {" "}
                        ({t(strings.exactLabel, { number })})
                      </span>
                    </>
                  }
                  onChange={(exact) => update(row.id, { exact })}
                />
              </span>
              <button
                aria-label={t(strings.removeRow, { number })}
                className={`${BUTTON_SMALL} mb-1`}
                onClick={() => setRows(rows.filter((r) => r.id !== row.id))}
                type="button"
              >
                ×
              </button>
            </div>
          );
        })}
        <div className="grid items-end gap-3 sm:grid-cols-2">
          <button
            className={BUTTON_SECONDARY}
            disabled={rows.length >= MAX_ROWS}
            onClick={() =>
              addRow({ lv: "", lu: "", rv: "", ru: "", exact: true })
            }
            type="button"
          >
            {strings.addRow}
          </button>
          <SelectField
            id="da-common"
            label={strings.addCommon}
            onChange={addCommon}
            options={[
              { value: "", label: strings.addCommonPlaceholder },
              ...COMMON_FACTORS.map((c) => ({
                value: c.id,
                label:
                  strings.commonLabels[
                    c.id as keyof typeof strings.commonLabels
                  ],
              })),
            ]}
            value=""
          />
        </div>
      </div>
      <SelectField
        id="da-sig"
        label={strings.sigFigs}
        onChange={setSigChoice}
        options={[
          { value: "match", label: strings.sigMatch },
          ...SIG_CHOICES.map((value) => ({
            value,
            label: tPlural(strings.sigChoice, locale, Number(value), {
              count: fmt(Number(value)),
            }).trim(),
          })),
        ]}
        value={sigChoice}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {evaluation.ok ? (
          <ChainResults
            sig={sig}
            specs={evaluation.specs}
            startUnits={evaluation.startUnits}
            startValue={evaluation.startValue}
            strings={strings}
            target={evaluation.target}
          />
        ) : (
          <Problem
            evaluation={evaluation}
            number={fmt(problemRow + 1)}
            strings={strings}
          />
        )}
      </section>
    </>
  );
}
